import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, Save, FileDown, Eye, RefreshCw, Clock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  BUILDER_STEPS, BuilderStepNav, CompletionBar, CareerPreviewPanel,
} from "@/components/career/builder/BuilderChrome";
import { StepIdentity, StepVision } from "@/components/career/builder/StepsIdentityVision";
import { StepRoadmap, StepObjectives } from "@/components/career/builder/StepsRoadmapObjectives";
import { StepReadiness, StepContact } from "@/components/career/builder/StepsReadinessContact";
import { translateFr as tfr } from "@/i18n";

const setPath = (obj, path, value) => {
  if (path.length === 1) return { ...obj, [path[0]]: value };
  return { ...obj, [path[0]]: setPath(obj?.[path[0]] || {}, path.slice(1), value) };
};

export default function CareerPlanBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: false });
  const [draft, setDraft] = useState(null);
  const [meta, setMeta] = useState(null); // { auto_readiness, dims, overall, velocity, completion }
  const [step, setStep] = useState(1);
  const [saveState, setSaveState] = useState("idle");
  const [generating, setGenerating] = useState(false);
  const [mobilePreview, setMobilePreview] = useState(false);
  const saveTimer = useRef(null);
  const draftRef = useRef(null);
  draftRef.current = draft;

  const load = useCallback(async () => {
    setState({ loading: true, error: false });
    try {
      const r = await api.get(`/players/${id}/career-plan/builder-draft`);
      setState({ loading: false, error: false, player: r.data.player, catalogs: r.data.catalogs });
      setDraft(r.data.draft);
      setMeta({ auto_readiness: r.data.auto_readiness, dims: r.data.dims, overall: r.data.overall,
                velocity: r.data.velocity, completion: r.data.completion });
      setStep(r.data.draft.current_step || 1);
    } catch (e) {
      if (e?.response?.status === 404) { navigate("/pipeline"); return; }
      setState({ loading: false, error: true });
    }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const persist = useCallback(async () => {
    if (!draftRef.current) return;
    setSaveState("saving");
    try {
      const r = await api.patch(`/players/${id}/career-plan/builder-draft`, { draft: draftRef.current });
      setMeta({ auto_readiness: r.data.auto_readiness, dims: r.data.dims, overall: r.data.overall,
                velocity: r.data.velocity, completion: r.data.completion });
      setSaveState("saved");
    } catch (e) {
      setSaveState(e?.response?.status === 402 ? "idle" : "error");
    }
  }, [id]);

  const update = useCallback((path, value) => {
    setDraft((d) => setPath(d, path, value));
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(persist, 1000);
  }, [persist]);
  useEffect(() => () => clearTimeout(saveTimer.current), []);

  const goStep = (n) => { setStep(n); update(["current_step"], n); };

  // Debounced preview (250 ms) for a soft instant refresh.
  const [previewDraft, setPreviewDraft] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => setPreviewDraft(draft), 250);
    return () => clearTimeout(t);
  }, [draft]);

  const generate = async () => {
    setGenerating(true);
    try {
      clearTimeout(saveTimer.current);
      await persist();
      const lang = localStorage.getItem("athlynx_lang") === "en" ? "en" : "fr";
      const r = await api.post(`/players/${id}/career-plan/builder-draft/generate`, { lang });
      const dl = await api.get(`/players/${id}/documents/${r.data.document_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([dl.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = r.data.filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${tfr("Plan de carrière généré")} (V${r.data.version}) ${tfr("et ajouté aux Documents")}`);
    } catch (e) {
      if (e?.response?.status !== 402) toast.error(e?.response?.data?.detail || tfr("Impossible de générer le PDF"));
    } finally { setGenerating(false); }
  };

  if (state.loading) {
    return (
      <div className="px-4 md:px-6 py-6 grid grid-cols-1 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.25fr)] gap-4" data-testid="builder-loading">
        <Skeleton className="h-[70vh] rounded-2xl" />
        <Skeleton className="h-[70vh] rounded-2xl hidden xl:block" />
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="py-16 text-center" data-testid="builder-error">
        <p className="text-sm text-muted-foreground">Impossible de charger le Career Plan Builder.</p>
        <Button variant="outline" onClick={load} className="mt-4 gap-2" data-testid="builder-retry">
          <RefreshCw className="h-4 w-4" /> Réessayer
        </Button>
      </div>
    );
  }
  if (!draft) return null;
  const { player, catalogs } = state;
  const stepProps = { draft, update, catalogs, meta, playerId: id };

  return (
    <div className="px-4 md:px-6 py-4 pb-24" data-testid="career-plan-builder">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3" aria-label="Fil d'Ariane">
        <button onClick={() => navigate(`/players/${id}/career-plan`)}
          className="rounded-full p-1.5 hover:bg-secondary transition-colors -ml-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-athlynx/40"
          aria-label={tfr("Retour au plan de carrière")} data-testid="builder-back">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <Link to="/pipeline" className="hover:text-foreground transition-colors">{tfr("Athlètes")}</Link>
        <span aria-hidden="true">/</span>
        <Link to={`/players/${id}/career-plan`} className="hover:text-foreground transition-colors">{player.name}</Link>
        <span aria-hidden="true">/</span>
        <span className="text-foreground font-medium">Career Plan Builder</span>
      </nav>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3 mb-4">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.22em] text-athlynx font-semibold">Athlynx</p>
          <h1 className="text-xl md:text-2xl font-semibold tracking-tight" data-testid="builder-title">Career Plan Builder</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {tfr("Générez une feuille de route professionnelle prête à partager avec les athlètes, clubs et partenaires.")}
          </p>
        </div>
        <div className="flex items-center flex-wrap gap-3 text-xs text-muted-foreground">
          <span className="tabular-nums font-medium text-foreground" data-testid="builder-step-indicator">
            Étape {step}/{BUILDER_STEPS.length}
          </span>
          <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> ≈ 3–5 minutes</span>
          <Button variant="outline" size="sm" className="h-9 gap-1.5 xl:hidden"
            onClick={() => setMobilePreview(true)} data-testid="builder-mobile-preview-btn">
            <Eye className="h-3.5 w-3.5" /> Aperçu
          </Button>
        </div>
      </div>

      {/* Workspace grid */}
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.25fr)] gap-4 items-start">
        <div className="flex flex-col min-w-0 rounded-2xl border border-border bg-card overflow-hidden xl:h-[calc(100dvh-250px)] xl:min-h-[540px]"
          data-testid="builder-editor-panel">
          <CompletionBar completion={meta?.completion} saveState={saveState} />
          <BuilderStepNav current={step} onSelect={goStep} />
          <div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-5">
            {step === 1 && <StepIdentity {...stepProps} />}
            {step === 2 && <StepVision {...stepProps} />}
            {step === 3 && <StepRoadmap {...stepProps} />}
            {step === 4 && <StepObjectives {...stepProps} />}
            {step === 5 && <StepReadiness {...stepProps} />}
            {step === 6 && <StepContact {...stepProps} />}
          </div>
          <div className="shrink-0 grid grid-cols-[1fr_auto_1fr] items-center gap-3 px-4 py-3 border-t border-border bg-card/95">
            <div>
              {step > 1 && (
                <Button variant="outline" size="sm" onClick={() => goStep(step - 1)} className="h-9" data-testid="builder-prev-btn">
                  {tfr("Étape précédente")}
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground tabular-nums" aria-live="polite">Étape {step} sur {BUILDER_STEPS.length}</p>
            <div className="text-right">
              {step < BUILDER_STEPS.length && (
                <Button size="sm" onClick={() => goStep(step + 1)} className="h-9 bg-athlynx hover:bg-athlynx/90 text-white"
                  data-testid="builder-next-btn">
                  {tfr("Étape suivante")}
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="xl:sticky xl:top-4 xl:h-[calc(100dvh-250px)] xl:min-h-[540px] min-w-0 flex">
          <CareerPreviewPanel draft={previewDraft || draft} meta={meta} catalogs={catalogs}
            mobileOpen={mobilePreview} onMobileClose={() => setMobilePreview(false)} />
        </div>
      </div>

      {/* Sticky action bar */}
      <div className="fixed bottom-0 inset-x-0 z-40 border-t border-border bg-card/95 backdrop-blur px-4 md:px-6 py-3"
        data-testid="builder-action-bar">
        <div className="flex items-center justify-between gap-3 max-w-[1600px] mx-auto">
          <Button variant="ghost" size="sm" className="h-9" onClick={() => navigate(`/players/${id}/career-plan`)}
            data-testid="builder-cancel-btn">{tfr("Annuler")}</Button>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-9 gap-1.5"
              onClick={() => { clearTimeout(saveTimer.current); persist(); }} data-testid="builder-save-btn">
              <Save className="h-3.5 w-3.5" /> Enregistrer le brouillon
            </Button>
            <Button variant="outline" size="sm" className="h-9 gap-1.5 xl:hidden"
              onClick={() => setMobilePreview(true)} data-testid="builder-preview-btn">
              <Eye className="h-3.5 w-3.5" /> Aperçu PDF
            </Button>
            <Button size="sm" className="h-9 gap-1.5 bg-athlynx hover:bg-athlynx/90 text-white"
              onClick={generate} disabled={generating} data-testid="builder-generate-btn">
              {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />}
              {tfr("Générer le Plan de Carrière")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
